import React from 'react';
import { Helmet } from '@lib/helmet-shim';
import { getRobotsMeta, LANGUAGE_PATH_MAP, SEO_CONFIG, siteConfig, SUPPORTED_LANGUAGES } from '../config';

export interface HreflangLink {
  hrefLang: string;
  href: string;
}

interface SeoHeadProps {
  title?: string;
  /** Full custom title — rendered as-is, without appending the site suffix. */
  rawTitle?: string;
  description?: string;
  pageSlug?: string; // 'terms', 'dmca', 'privacy', etc.
  image?: string;
  url?: string;
  canonicalUrl?: string;
  jsonLd?: Record<string, unknown>;
  /**
   * Current page path (e.g. '/', '/pokerogue', '/category/pokemon').
   * When provided, hreflang alternate links are generated for all supported
   * languages using the LANGUAGE_PATH_MAP URL scheme.
   * Omit for noindex pages (terms, dmca, privacy) or pages without locale variants.
   */
  pagePath?: string;
}

/**
 * SEO Head Component
 * Handles all meta tags including dynamic robots meta for different page types
 * Automatically applies noindex,nofollow for terms/dmca/privacy pages
 */
const SeoHead: React.FC<SeoHeadProps> = ({
  title,
  rawTitle,
  description,
  pageSlug,
  image = SEO_CONFIG.DEFAULT_IMAGE,
  url,
  canonicalUrl,
  jsonLd,
  pagePath,
}) => {
  const robotsMeta = getRobotsMeta(pageSlug);
  const fullTitle = rawTitle
    ?? (title ? `${title}${SEO_CONFIG.TITLE_SEPARATOR}${SEO_CONFIG.TITLE_SUFFIX}` : undefined);
  const canonical = canonicalUrl || url || (typeof window !== 'undefined' ? window.location.href : siteConfig.siteUrl);

  // Build hreflang alternate links when a pagePath is supplied
  const hreflangLinks: HreflangLink[] = pagePath
    ? [
        ...SUPPORTED_LANGUAGES.map((lang) => {
          const prefix = LANGUAGE_PATH_MAP[lang.code] ?? `/${lang.code}`;
          const href =
            prefix === '/'
              ? `${siteConfig.siteUrl}${pagePath}`
              : `${siteConfig.siteUrl}${prefix}${pagePath === '/' ? '' : pagePath}`;
          return { hrefLang: lang.hreflang, href };
        }),
        { hrefLang: 'x-default', href: `${siteConfig.siteUrl}${pagePath}` },
      ]
    : [];

  return (
    <Helmet>
      {fullTitle && <title>{fullTitle}</title>}
      {description && <meta name="description" content={description} />}
      <meta name="robots" content={robotsMeta} />
      <link rel="canonical" href={canonical} />

      {/* Hreflang alternate links */}
      {hreflangLinks.map(({ hrefLang, href }) => (
        <link key={hrefLang} rel="alternate" hrefLang={hrefLang} href={href} />
      ))}

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={siteConfig.siteName} />
      {fullTitle && <meta property="og:title" content={fullTitle} />}
      {description && <meta property="og:description" content={description} />}
      <meta property="og:image" content={image} />
      <meta property="og:locale" content={SEO_CONFIG.OG_LOCALE} />
      <meta property="og:url" content={canonical} />

      {/* Twitter Card */}
      <meta name="twitter:card" content={SEO_CONFIG.TWITTER_CARD_TYPE} />
      {fullTitle && <meta name="twitter:title" content={fullTitle} />}
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={image} />

      {/* JSON-LD Structured Data */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
};

export default SeoHead;
